/**
 * @fileoverview Zod schema for the change-password form.
 * @description Runs before the change-password request is sent so obviously-invalid input never
 * reaches the API layer. Password complexity is still enforced server-side, but — like the
 * set-password schema — the new password is also checked against the live
 * GET /identity/password-policy response (via passwordMeetsPolicy), so a password the visible rule
 * checklist shows as non-compliant fails submit too, not just looks wrong. The confirm-password
 * field is sent alongside (the API re-checks the match). Every issue message is a translation key
 * (per this repo's i18n rule for validators), translated only at the display call site in
 * ChangePasswordForm.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { z } from 'zod'

import { passwordMeetsPolicy } from '@/features/auth/utils/password-policy.utils'

import type { PasswordPolicy } from '@/features/auth/services/password-policy.service'

/**
 * @description Builds the change-password schema against the currently fetched password policy.
 * Before the policy has loaded, only the required/match checks apply — matching the checklist's
 * own "nothing to show yet" state.
 * @param {PasswordPolicy | undefined} policy - The fetched password policy, once loaded.
 * @returns {z.ZodType<{ currentPassword: string; newPassword: string; confirmPassword: string }>}
 * The schema to resolve the form against.
 */
export function buildChangePasswordFormSchema(policy: PasswordPolicy | undefined) {
  return z
    .object({
      currentPassword: z.string().min(1, 'settings.changePassword.errors.currentPasswordRequired'),
      newPassword: z.string().min(1, 'settings.changePassword.errors.newPasswordRequired'),
      confirmPassword: z.string().min(1, 'settings.changePassword.errors.confirmPasswordRequired'),
    })
    .superRefine((values, ctx) => {
      if (
        policy &&
        values.newPassword.length > 0 &&
        !passwordMeetsPolicy(policy, values.newPassword)
      ) {
        ctx.addIssue({
          code: 'custom',
          path: ['newPassword'],
          message: 'acceptInvitation.errors.passwordPolicyNotMet',
        })
      }

      if (values.confirmPassword.length > 0 && values.newPassword !== values.confirmPassword) {
        ctx.addIssue({
          code: 'custom',
          path: ['confirmPassword'],
          message: 'settings.changePassword.errors.passwordsDontMatch',
        })
      }
    })
}

export type ChangePasswordFormValues = z.infer<ReturnType<typeof buildChangePasswordFormSchema>>
